// src/pages/consulting/ConsultingAbout.js
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import ConsultingLayout from "./ConsultingLayout.js";
import {
  Target,
  Lightbulb,
  ShieldCheck,
  ArrowRight,
  Crown,
  Users,
  Briefcase,
  Sparkles,
  CheckCircle2,
} from "lucide-react";

const STYLES = `
  @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700&family=Bricolage+Grotesque:wght@400;500;600;700;800&display=swap');

  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

  .ca-page {
    --bg:        #04050a;
    --surface:   #080b12;
    --surface-2: #0d1018;
    --border:    rgba(255,255,255,0.06);
    --border-2:  rgba(255,255,255,0.10);
    --accent:    #6366f1;
    --accent-2:  #8b5cf6;
    --green:     #10b981;
    --white:     #f8fafc;
    --muted:     #475569;
    --muted-2:   #64748b;
    --text:      #cbd5e1;

    background: var(--bg);
    color: var(--white);
    min-height: 100vh;
    font-family: 'Inter', sans-serif;
    overflow-x: hidden;
    -webkit-font-smoothing: antialiased;
  }

  /* ── Background ── */
  .ca-bg-canvas {
    position: fixed;
    inset: 0;
    z-index: 0;
    pointer-events: none;
  }

  .ca-bg-grid {
    position: absolute;
    inset: 0;
    background-image:
      linear-gradient(rgba(99,102,241,0.03) 1px, transparent 1px),
      linear-gradient(90deg, rgba(99,102,241,0.03) 1px, transparent 1px);
    background-size: 60px 60px;
    mask-image: radial-gradient(ellipse 80% 60% at 50% 0%, black 40%, transparent 100%);
  }

  .ca-bg-orb-1 {
    position: absolute;
    top: -200px; left: 50%;
    transform: translateX(-50%);
    width: 800px; height: 600px;
    background: radial-gradient(ellipse, rgba(99,102,241,0.09) 0%, transparent 70%);
    animation: orb-pulse 8s ease-in-out infinite;
  }

  .ca-bg-orb-2 {
    position: absolute;
    bottom: 10%; right: -100px;
    width: 400px; height: 400px;
    background: radial-gradient(circle, rgba(139,92,246,0.05) 0%, transparent 70%);
    animation: orb-pulse 12s ease-in-out infinite reverse;
  }

  @keyframes orb-pulse {
    0%, 100% { opacity: 1; transform: translateX(-50%) scale(1); }
    50%       { opacity: 0.6; transform: translateX(-50%) scale(1.06); }
  }

  /* ── Shell ── */
  .ca-shell {
    position: relative;
    z-index: 1;
    max-width: 1000px;
    margin: 0 auto;
    padding: 0 32px 100px;
  }

  /* ── Header ── */
  .ca-header {
    padding: 72px 0 60px;
    text-align: center;
    animation: fade-up 0.6s ease both;
  }

  @keyframes fade-up {
    from { opacity: 0; transform: translateY(16px); }
    to   { opacity: 1; transform: translateY(0); }
  }

  .ca-header-tag {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    font-size: 11px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.14em;
    color: var(--muted-2);
    margin-bottom: 24px;
  }

  .ca-header-tag-dot {
    width: 6px; height: 6px;
    border-radius: 50%;
    background: var(--accent);
    box-shadow: 0 0 8px rgba(99,102,241,0.8);
    animation: dot-blink 2s ease-in-out infinite;
  }

  @keyframes dot-blink {
    0%, 100% { opacity: 1; }
    50%       { opacity: 0.3; }
  }

  .ca-h1 {
    font-family: 'Bricolage Grotesque', sans-serif;
    font-size: clamp(2.4rem, 6vw, 4rem);
    font-weight: 800;
    line-height: 1.08;
    letter-spacing: -0.04em;
    margin-bottom: 20px;
    color: var(--white);
  }

  .ca-h1 span {
    background: linear-gradient(135deg, #a5b4fc 0%, #818cf8 40%, #6366f1 70%, #8b5cf6 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }

  .ca-header-sub {
    color: var(--muted-2);
    max-width: 480px;
    margin: 0 auto;
    font-size: 15.5px;
    line-height: 1.65;
  }

  /* ── Quote card ── */
  .ca-quote {
    position: relative;
    margin-bottom: 16px;
    border-radius: 20px;
    padding: 40px 48px;
    background: var(--surface);
    border: 1px solid var(--border);
    overflow: hidden;
    animation: fade-up 0.6s 0.1s ease both;
  }

  .ca-quote::before {
    content: '';
    position: absolute;
    top: 0; left: 0; right: 0;
    height: 1px;
    background: linear-gradient(90deg, transparent, rgba(99,102,241,0.5), transparent);
  }

  .ca-quote::after {
    content: '"';
    position: absolute;
    top: -20px; left: 32px;
    font-size: 160px;
    font-family: 'Bricolage Grotesque', sans-serif;
    font-weight: 800;
    color: rgba(99,102,241,0.06);
    line-height: 1;
    pointer-events: none;
    user-select: none;
  }

  .ca-quote-text {
    font-family: 'Bricolage Grotesque', sans-serif;
    font-size: clamp(1.1rem, 2.5vw, 1.45rem);
    font-weight: 600;
    line-height: 1.5;
    color: var(--white);
    position: relative;
    z-index: 1;
    letter-spacing: -0.02em;
  }

  .ca-quote-glow {
    position: absolute;
    left: -80px; top: -80px;
    width: 300px; height: 300px;
    background: radial-gradient(circle, rgba(99,102,241,0.07) 0%, transparent 70%);
    pointer-events: none;
  }

  /* ── Bento grid ── */
  .ca-bento {
    display: grid;
    grid-template-columns: repeat(12, 1fr);
    gap: 14px;
    margin-bottom: 48px;
  }

  /* ── Pillar cards ── */
  .ca-pillar {
    grid-column: span 4;
    border-radius: 20px;
    padding: 28px;
    background: var(--surface);
    border: 1px solid var(--border);
    position: relative;
    overflow: hidden;
    transition: all 0.35s cubic-bezier(0.16, 1, 0.3, 1);
    animation: fade-up 0.6s ease both;
  }

  .ca-pillar:nth-child(1) { animation-delay: 0.15s; }
  .ca-pillar:nth-child(2) { animation-delay: 0.22s; }
  .ca-pillar:nth-child(3) { animation-delay: 0.29s; }

  .ca-pillar::before {
    content: '';
    position: absolute;
    top: 0; left: 0; right: 0;
    height: 1px;
    background: linear-gradient(90deg, transparent, rgba(99,102,241,0.4), transparent);
    opacity: 0;
    transition: opacity 0.3s;
  }

  .ca-pillar:hover {
    border-color: rgba(99,102,241,0.25);
    transform: translateY(-5px);
    box-shadow: 0 24px 60px rgba(0,0,0,0.45), 0 0 0 1px rgba(99,102,241,0.1);
    background: var(--surface-2);
  }

  .ca-pillar:hover::before { opacity: 1; }

  .ca-pillar-glow {
    position: absolute;
    inset: 0;
    background: radial-gradient(ellipse 80% 80% at 50% 120%, rgba(99,102,241,0.08) 0%, transparent 70%);
    pointer-events: none;
  }

  .ca-pillar-icon {
    width: 44px; height: 44px;
    border-radius: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(99,102,241,0.12);
    border: 1px solid rgba(99,102,241,0.2);
    color: #a5b4fc;
    margin-bottom: 20px;
    transition: all 0.3s;
  }

  .ca-pillar:hover .ca-pillar-icon {
    background: rgba(99,102,241,0.2);
    border-color: rgba(99,102,241,0.35);
    box-shadow: 0 0 20px rgba(99,102,241,0.2);
  }

  .ca-pillar-num {
    position: absolute;
    top: 22px; right: 24px;
    font-size: 11px;
    font-weight: 700;
    color: var(--muted);
    letter-spacing: 0.06em;
    transition: color 0.3s;
  }

  .ca-pillar:hover .ca-pillar-num { color: rgba(99,102,241,0.6); }

  .ca-pillar h3 {
    font-family: 'Bricolage Grotesque', sans-serif;
    font-size: 17px;
    font-weight: 700;
    letter-spacing: -0.02em;
    color: var(--white);
    margin-bottom: 10px;
  }

  .ca-pillar p {
    font-size: 13.5px;
    color: var(--muted-2);
    line-height: 1.65;
  }

  /* ── Stats band ── */
  .ca-stats {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 14px;
    margin-bottom: 14px;
    animation: fade-up 0.6s 0.35s ease both;
  }

  .ca-stat {
    border-radius: 16px;
    padding: 24px 28px;
    background: var(--surface);
    border: 1px solid var(--border);
    position: relative;
    overflow: hidden;
    transition: all 0.3s ease;
  }

  .ca-stat:hover {
    border-color: rgba(99,102,241,0.2);
    transform: translateY(-3px);
  }

  .ca-stat-val {
    font-family: 'Bricolage Grotesque', sans-serif;
    font-size: 2.4rem;
    font-weight: 800;
    letter-spacing: -0.04em;
    background: linear-gradient(135deg, #a5b4fc, #6366f1);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    line-height: 1;
    margin-bottom: 6px;
  }

  .ca-stat-label {
    font-size: 12.5px;
    color: var(--muted-2);
    font-weight: 500;
    line-height: 1.4;
  }

  /* ── Team leadership section ── */
  .ca-team {
    display: grid;
    grid-template-columns: 320px 1fr;
    gap: 18px;
    margin: 18px 0 14px;
    animation: fade-up 0.6s 0.36s ease both;
  }

  .ca-founder-card,
  .ca-team-card,
  .ca-values-card {
    border-radius: 20px;
    background: var(--surface);
    border: 1px solid var(--border);
    position: relative;
    overflow: hidden;
  }

  .ca-founder-card::before,
  .ca-team-card::before,
  .ca-values-card::before {
    content: '';
    position: absolute;
    top: 0; left: 0; right: 0;
    height: 1px;
    background: linear-gradient(90deg, transparent, rgba(99,102,241,0.45), transparent);
  }

  .ca-founder-card {
    padding: 18px;
  }

  .ca-founder-photo-wrap {
    position: relative;
    width: 100%;
    height: 360px;
    border-radius: 18px;
    overflow: hidden;
    background: linear-gradient(180deg, rgba(18,25,45,0.9), rgba(10,12,20,0.96));
    border: 1px solid rgba(255,255,255,0.06);
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .ca-founder-photo {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }

  .ca-founder-placeholder {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 14px;
    color: var(--muted-2);
    text-align: center;
    padding: 20px;
  }

  .ca-founder-placeholder-badge {
    width: 72px;
    height: 72px;
    border-radius: 20px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(99,102,241,0.14);
    border: 1px solid rgba(99,102,241,0.22);
    color: #a5b4fc;
  }

  .ca-founder-meta {
    margin-top: 18px;
    display: flex;
    flex-direction: column;
    gap: 10px;
  }

  .ca-founder-role {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    width: fit-content;
    padding: 8px 12px;
    border-radius: 999px;
    font-size: 11px;
    font-weight: 800;
    text-transform: uppercase;
    letter-spacing: 0.12em;
    color: #c4b5fd;
    background: rgba(99,102,241,0.12);
    border: 1px solid rgba(99,102,241,0.18);
  }

  .ca-founder-name {
    font-family: 'Bricolage Grotesque', sans-serif;
    font-size: 1.45rem;
    font-weight: 700;
    letter-spacing: -0.03em;
    color: var(--white);
  }

  .ca-founder-desc {
    font-size: 14px;
    color: var(--muted-2);
    line-height: 1.65;
  }

  .ca-team-card {
    padding: 26px;
  }

  .ca-section-top {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-bottom: 10px;
    color: var(--white);
  }

  .ca-section-top h3 {
    font-family: 'Bricolage Grotesque', sans-serif;
    font-size: 1.45rem;
    font-weight: 700;
    letter-spacing: -0.03em;
  }

  .ca-section-sub {
    font-size: 14px;
    color: var(--muted-2);
    line-height: 1.65;
    margin-bottom: 18px;
    max-width: 640px;
  }

  .ca-team-grid {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 14px;
  }

  .ca-team-role {
    border-radius: 16px;
    padding: 18px;
    background: rgba(255,255,255,0.02);
    border: 1px solid rgba(255,255,255,0.06);
    transition: all 0.25s ease;
  }

  .ca-team-role:hover {
    background: rgba(99,102,241,0.05);
    border-color: rgba(99,102,241,0.18);
    transform: translateY(-2px);
  }

  .ca-team-role-top {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 10px;
    margin-bottom: 10px;
  }

  .ca-team-role h4 {
    font-size: 15px;
    font-weight: 700;
    color: var(--white);
    letter-spacing: -0.01em;
  }

  .ca-team-level {
    font-size: 11px;
    font-weight: 800;
    color: #a5b4fc;
    background: rgba(99,102,241,0.12);
    border: 1px solid rgba(99,102,241,0.18);
    padding: 5px 8px;
    border-radius: 999px;
    white-space: nowrap;
  }

  .ca-team-role p {
    font-size: 13.5px;
    color: var(--muted-2);
    line-height: 1.65;
  }

  .ca-values-card {
    padding: 26px;
    margin-top: 14px;
    animation: fade-up 0.6s 0.39s ease both;
  }

  .ca-values-grid {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 14px;
    margin-top: 18px;
  }

  .ca-value {
    border-radius: 16px;
    padding: 18px;
    background: rgba(255,255,255,0.02);
    border: 1px solid rgba(255,255,255,0.06);
  }

  .ca-value-head {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-bottom: 10px;
    color: var(--white);
  }

  .ca-value-head h4 {
    font-size: 15px;
    font-weight: 700;
    letter-spacing: -0.01em;
  }

  .ca-value p {
    font-size: 13.5px;
    color: var(--muted-2);
    line-height: 1.65;
  }

  /* ── CTA strip ── */
  .ca-cta-strip {
    border-radius: 20px;
    padding: 44px 52px;
    background: var(--surface);
    border: 1px solid var(--border);
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 32px;
    position: relative;
    overflow: hidden;
    animation: fade-up 0.6s 0.4s ease both;
  }

  .ca-cta-strip::before {
    content: '';
    position: absolute;
    top: 0; left: 0; right: 0;
    height: 1px;
    background: linear-gradient(90deg, transparent, rgba(99,102,241,0.5), transparent);
  }

  .ca-cta-strip::after {
    content: '';
    position: absolute;
    inset: 0;
    background: radial-gradient(ellipse 60% 100% at 0% 50%, rgba(99,102,241,0.06) 0%, transparent 70%);
    pointer-events: none;
  }

  .ca-cta-text h3 {
    font-family: 'Bricolage Grotesque', sans-serif;
    font-size: 1.55rem;
    font-weight: 700;
    letter-spacing: -0.03em;
    color: var(--white);
    margin-bottom: 8px;
  }

  .ca-cta-text p {
    font-size: 14px;
    color: var(--muted-2);
    line-height: 1.6;
    max-width: 380px;
  }

  .ca-cta-btn {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 14px 28px;
    border-radius: 10px;
    font-size: 14px;
    font-weight: 600;
    text-decoration: none;
    background: linear-gradient(135deg, #6366f1, #8b5cf6);
    color: white;
    border: 1px solid rgba(139,92,246,0.3);
    box-shadow: 0 0 30px rgba(99,102,241,0.25), 0 4px 16px rgba(0,0,0,0.3);
    white-space: nowrap;
    flex-shrink: 0;
    position: relative;
    z-index: 1;
    transition: all 0.25s ease;
  }

  .ca-cta-btn:hover {
    transform: translateY(-2px);
    box-shadow: 0 0 50px rgba(99,102,241,0.4), 0 8px 24px rgba(0,0,0,0.4);
  }

  /* ── Scroll Navigator ── */
  .ca-scroll-nav {
    position: fixed;
    right: 24px;
    top: 50%;
    transform: translateY(-50%);
    z-index: 1000;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 10px;
  }

  .ca-scroll-btn {
    width: 40px; height: 40px;
    border-radius: 12px;
    background: rgba(13,16,24,0.85);
    border: 1px solid rgba(255,255,255,0.08);
    backdrop-filter: blur(12px);
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: all 0.25s ease;
    color: rgba(255,255,255,0.5);
    outline: none;
    box-shadow: 0 4px 20px rgba(0,0,0,0.4);
  }

  .ca-scroll-btn:hover {
    background: rgba(99,102,241,0.15);
    border-color: rgba(99,102,241,0.35);
    color: #818cf8;
    transform: scale(1.08);
    box-shadow: 0 0 20px rgba(99,102,241,0.2), 0 4px 20px rgba(0,0,0,0.4);
  }

  .ca-scroll-btn.disabled { opacity: 0.25; pointer-events: none; }

  .ca-scroll-btn svg {
    width: 16px; height: 16px;
    stroke: currentColor;
    stroke-width: 2.2;
    fill: none;
    stroke-linecap: round;
    stroke-linejoin: round;
  }

  .ca-scroll-track {
    width: 2px; height: 80px;
    background: rgba(255,255,255,0.06);
    border-radius: 2px;
    overflow: hidden;
    position: relative;
  }

  .ca-scroll-fill {
    width: 100%;
    background: linear-gradient(180deg, #6366f1, #8b5cf6);
    border-radius: 2px;
    transition: height 0.1s linear;
    box-shadow: 0 0 8px rgba(99,102,241,0.6);
    position: absolute;
    top: 0; left: 0;
  }

  /* ── Responsive ── */
  @media (max-width: 960px) {
    .ca-team {
      grid-template-columns: 1fr;
    }
  }

  @media (max-width: 768px) {
    .ca-pillar   { grid-column: span 12; }
    .ca-stats    { grid-template-columns: 1fr; }
    .ca-team-grid,
    .ca-values-grid { grid-template-columns: 1fr; }
    .ca-cta-strip { flex-direction: column; padding: 36px 28px; text-align: center; }
    .ca-cta-text p { max-width: 100%; }
    .ca-quote    { padding: 32px 28px; }
    .ca-scroll-nav { display: none; }
    .ca-founder-photo-wrap { height: 300px; }
  }
`;

/* ── Scroll Navigator ── */
function ScrollNavigator() {
  const [progress, setProgress] = useState(0);
  const [atTop, setAtTop]       = useState(true);
  const [atBottom, setAtBottom] = useState(false);

  useEffect(() => {
    const onScroll = () => {
      const scrollTop = window.scrollY;
      const docHeight = document.documentElement.scrollHeight - window.innerHeight;
      const pct       = docHeight > 0 ? (scrollTop / docHeight) * 100 : 0;
      setProgress(pct);
      setAtTop(scrollTop <= 10);
      setAtBottom(pct >= 98);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <div className="ca-scroll-nav">
      <button
        className={`ca-scroll-btn ${atTop ? "disabled" : ""}`}
        onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
      >
        <svg viewBox="0 0 24 24"><polyline points="18 15 12 9 6 15"/></svg>
      </button>
      <div className="ca-scroll-track">
        <div className="ca-scroll-fill" style={{ height: `${progress}%` }}/>
      </div>
      <button
        className={`ca-scroll-btn ${atBottom ? "disabled" : ""}`}
        onClick={() => window.scrollTo({ top: document.documentElement.scrollHeight, behavior: "smooth" })}
      >
        <svg viewBox="0 0 24 24"><polyline points="6 9 12 15 18 9"/></svg>
      </button>
    </div>
  );
}

const STATS = [
  { val: "15+",  label: "Projets IT livrés avec succès" },
  { val: "95%",  label: "Taux de satisfaction client" },
  { val: "48h",  label: "Délai moyen de première réponse" },
];

const PILLARS = [
  {
    icon: Target,
    title: "Approche",
    desc: "Audit flash, recommandation directe et exécution millimétrée. Nous identifions les leviers à fort impact et agissons sans délai.",
  },
  {
    icon: Lightbulb,
    title: "Expertise",
    desc: "Architectures des systèmes d’information, AMOA complexe et stratégie Data & BI. Une expertise technique et fonctionnelle au service de vos ambitions métiers.",
  },
  {
    icon: ShieldCheck,
    title: "Engagement",
    desc: "Garantie d'adoption terrain et pérennité des solutions. Nous restons à vos côtés bien après la livraison.",
  },
];

const TEAM_ROLES = [
  {
    title: "CEO & Fondateur",
    
    desc: "Définit la vision stratégique du cabinet, pilote les orientations globales et supervise les missions à forte valeur ajoutée.",
  },
  {
    title: "Consultants seniors",
    
    desc: "Interviennent sur le cadrage, l’analyse métier, la transformation digitale, la gouvernance et le pilotage de projet.",
  },
  {
    title: "Experts SI / ERP & Data ",
    
    desc: "Apportent une expertise fonctionnelle et technique sur les systèmes d’information, l’intégration et l’optimisation des outils.",
  },
  {
    title: "Support projet & relation client",
    
    desc: "Assurent le suivi opérationnel, la coordination, la qualité de service et la continuité de l’accompagnement client.",
  },
];

const VALUES = [
  {
    icon: CheckCircle2,
    title: "Clarté",
    desc: "Des recommandations lisibles, structurées et directement exploitables pour la prise de décision.",
  },
  {
    icon: Sparkles,
    title: "Impact",
    desc: "Nous cherchons des résultats concrets, mesurables et utiles pour les équipes comme pour la direction.",
  },
  {
    icon: Briefcase,
    title: "Rigueur",
    desc: "Une exécution sérieuse, pilotée et alignée avec les enjeux métiers, techniques et organisationnels.",
  },
  {
    icon: Users,
    title: "Proximité",
    desc: "Un accompagnement humain, engagé et ancré dans la réalité du terrain et des besoins du client.",
  },
];

export default function ConsultingAbout() {
  return (
    <ConsultingLayout>
      <style>{STYLES}</style>
      <ScrollNavigator />

      <main className="ca-page">

        {/* Background */}
        <div className="ca-bg-canvas">
          <div className="ca-bg-grid"/>
          <div className="ca-bg-orb-1"/>
          <div className="ca-bg-orb-2"/>
        </div>

        <div className="ca-shell">

          {/* ── Header ── */}
          <header className="ca-header">
            <div className="ca-header-tag">
              <span className="ca-header-tag-dot"/>
              Vision 2026
            </div>
            <h1 className="ca-h1">
              L'expertise IT <span>sans compromis.</span>
            </h1>
            <p className="ca-header-sub">
              HDC Consulting redéfinit l'accompagnement digital par la clarté
              technique et la rigueur opérationnelle.
            </p>
          </header>

          {/* ── Quote ── */}
          <div className="ca-quote">
            <div className="ca-quote-glow"/>
            <p className="ca-quote-text">
              "Nous ne nous contentons pas de conseiller, nous activons le changement
              au cœur de vos processus métiers."
            </p>
          </div>

          {/* ── Stats ── */}
          <div className="ca-stats" style={{ marginTop: 14 }}>
            {STATS.map((s, i) => (
              <div className="ca-stat" key={i}>
                <div className="ca-stat-val">{s.val}</div>
                <div className="ca-stat-label">{s.label}</div>
              </div>
            ))}
          </div>

          {/* ── Pillars ── */}
          <div className="ca-bento" style={{ marginTop: 14, marginBottom: 14 }}>
            {PILLARS.map((p, i) => {
              const Icon = p.icon;
              return (
                <div className="ca-pillar" key={i}>
                  <div className="ca-pillar-glow"/>
                  <span className="ca-pillar-num">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <div className="ca-pillar-icon">
                    <Icon size={19}/>
                  </div>
                  <h3>{p.title}</h3>
                  <p>{p.desc}</p>
                </div>
              );
            })}
          </div>

          {/* ── Founder + Team ── */}
          <section className="ca-team">
            <div className="ca-founder-card">
              <div className="ca-founder-photo-wrap">
                <img
  src="/mody-hady-barry-clean.jpg"
  alt="CEO HDC Consulting"
  style={{
    width: "100%",
    maxWidth: "320px",
    height: "420px",
    objectFit: "cover",
    objectPosition: "center top",
    borderRadius: "16px",
    border: "1px solid rgba(255,255,255,0.08)",
    display: "block",
  }}
/>
                <div className="ca-founder-placeholder" style={{ display: "none" }}>
                  <div className="ca-founder-placeholder-badge">
                    <Crown size={32} />
                  </div>
                  <div>
                    <div style={{ fontWeight: 700, color: "#f8fafc", marginBottom: 6 }}>
                      Photo du CEO
                    </div>
                    <div style={{ fontSize: 13, lineHeight: 1.6 }}>
                      Ajoute l’image dans le dossier <strong>public</strong> avec le nom :
                      <br />
                      <strong>//mody-hady-barry-clean.jpg</strong>
                    </div>
                  </div>
                </div>
              </div>

              <div className="ca-founder-meta">
                <div className="ca-founder-role">
                  <Crown size={14} />
                  CEO & Fondateur
                </div>
                <div className="ca-founder-name">Mody Hady BARRY</div>
                <p className="ca-founder-desc">
                Fondateur du Groupe HDC, expert en systèmes d’information et en management,
                j’accompagne les organisations petites, moyennes et grandes entreprises dans leurs transformations digitales et le pilotage de leurs projets.             </p>
              </div>
            </div>

            <div className="ca-team-card">
              <div className="ca-section-top">
                <Users size={20} />
                <h3>Direction & organisation de l’équipe</h3>
              </div>
              <p className="ca-section-sub">
                Notre organisation repose sur une hiérarchie claire, une complémentarité
                des expertises et une exécution rigoureuse, afin d’apporter aux clients
                un accompagnement stratégique, fonctionnel et opérationnel.
              </p>

              <div className="ca-team-grid">
                {TEAM_ROLES.map((item, i) => (
                  <div className="ca-team-role" key={i}>
                    <div className="ca-team-role-top">
                      <h4>{item.title}</h4>
                      <span className="ca-team-level">{item.level}</span>
                    </div>
                    <p>{item.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* ── Values ── */}
          <section className="ca-values-card">
            <div className="ca-section-top">
              <ShieldCheck size={20} />
              <h3>Nos valeurs de fonctionnement</h3>
            </div>
            <p className="ca-section-sub">
              Nous combinons leadership, expertise métier, exigence de qualité et proximité
              client pour construire des solutions durables et crédibles.
            </p>

            <div className="ca-values-grid">
              {VALUES.map((item, i) => {
                const Icon = item.icon;
                return (
                  <div className="ca-value" key={i}>
                    <div className="ca-value-head">
                      <Icon size={18} />
                      <h4>{item.title}</h4>
                    </div>
                    <p>{item.desc}</p>
                  </div>
                );
              })}
            </div>
          </section>

          {/* ── CTA strip ── */}
          <div className="ca-cta-strip">
            <div className="ca-cta-text">
              <h3>Prêt à passer à l'action ?</h3>
              <p>
                Échangeons sur vos enjeux lors d'un premier appel stratégique,
                sans engagement. Nos experts répondent sous 48h.
              </p>
            </div>
            <Link to="/consulting/contact" className="ca-cta-btn">
              Démarrer un projet <ArrowRight size={15}/>
            </Link>
          </div>

        </div>
      </main>

    

    </ConsultingLayout>
  );
}